/**
 * Operator mutations (back-office). NON-OPTIMISTIC: provisioning creates real
 * cross-tenant records — wait for the backend.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap, unwrapRaw } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type HotelFact,
  hotelFactSchema,
  type ImpersonationResult,
  impersonationResultSchema,
  type ProvisionedPartner,
  provisionedPartnerSchema,
} from '../responses';

/** Body for provisioning a new partner (tenant + first hotel + first admin). */
export interface ProvisionPartnerBody {
  partner: { name: string; slug: string };
  hotel: { name: string; slug: string; timezone: string };
  admin: { email: string; fullName?: string; password: string };
}

/** `POST /operator/partners` — register a new partner. Requires `admin:provision`. */
export function useProvisionPartner(): UseMutationResult<
  ProvisionedPartner,
  unknown,
  ProvisionPartnerBody
> {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (body: ProvisionPartnerBody) => {
      const result = await client.POST('/api/v1/operator/partners', { body, parseAs: 'json' });
      return unwrap(result, provisionedPartnerSchema);
    },
  });
}

/**
 * Body for starting an operator impersonation session. The operator MUST justify
 * the session with a `reason` (12–500 chars) and a `supportTicketId` matching the
 * support-ticket pattern; `durationSeconds` is optional (300–3600, default 3600).
 * The backend re-validates and clamps all of these — these bounds are mirrored only
 * for fast client-side feedback (see SUPPORT_TICKET_PATTERN).
 */
export interface ImpersonateBody {
  targetUserId: string;
  tenantId: string;
  hotelId?: string;
  reason: string;
  supportTicketId: string;
  durationSeconds?: number;
}

/** Support-ticket key pattern, mirrored from the backend DTO (e.g. `HOS-4830`). */
export const SUPPORT_TICKET_PATTERN = /^[A-Z]{2,5}-\d{1,8}$/;

/** Impersonation duration bounds in seconds, mirrored from the backend DTO. */
export const MIN_IMPERSONATION_DURATION_SECONDS = 300;
export const MAX_IMPERSONATION_DURATION_SECONDS = 3600;
export const DEFAULT_IMPERSONATION_DURATION_SECONDS = 3600;

/**
 * `POST /operator/impersonate` — start an operator impersonation session.
 * Returns a short-lived token scoped to the target user. Requires
 * `admin:impersonate`.
 */
export function useImpersonate(): UseMutationResult<ImpersonationResult, unknown, ImpersonateBody> {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (body: ImpersonateBody) => {
      const result = await client.POST('/api/v1/operator/impersonate', { body, parseAs: 'json' });
      return unwrap(result, impersonationResultSchema);
    },
  });
}

/**
 * `POST /operator/impersonate/stop` — end the current impersonation session.
 * Returns void; the caller should invalidate `me` and redirect to the operator
 * dashboard.
 */
export function useStopImpersonation(): UseMutationResult<void, unknown, void> {
  const client = useApiClient();
  return useMutation({
    mutationFn: async () => {
      const result = await client.POST('/api/v1/operator/impersonate/stop', {
        parseAs: 'json',
      });
      unwrapRaw(result);
    },
  });
}

export interface UpsertOperatorHotelFactVariables {
  category: string;
  value: string;
}

/** `PUT /operator/hotels/{id}/setup/facts/{category}` — audited write on behalf of the partner. Requires `admin:hotel_configure`. */
export function useUpsertOperatorHotelFact(
  hotelId: string,
): UseMutationResult<HotelFact, unknown, UpsertOperatorHotelFactVariables> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ category, value }: UpsertOperatorHotelFactVariables) => {
      const result = await client.PUT('/api/v1/operator/hotels/{id}/setup/facts/{category}', {
        params: { path: { id: hotelId, category } },
        body: { value },
        parseAs: 'json',
      });
      return unwrap(result, hotelFactSchema);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.operatorHotelFacts(hotelId) });
    },
  });
}

export interface DeleteOperatorHotelFactVariables {
  category: string;
}

/** `DELETE /operator/hotels/{id}/setup/facts/{category}` — audited delete. Requires `admin:hotel_configure`. */
export function useDeleteOperatorHotelFact(
  hotelId: string,
): UseMutationResult<void, unknown, DeleteOperatorHotelFactVariables> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ category }: DeleteOperatorHotelFactVariables) => {
      const result = await client.DELETE('/api/v1/operator/hotels/{id}/setup/facts/{category}', {
        params: { path: { id: hotelId, category } },
        parseAs: 'json',
      });
      return unwrapRaw(result);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.operatorHotelFacts(hotelId) });
    },
  });
}
